import { useEffect, useMemo, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { motion } from "framer-motion"
import { z } from "zod"
import { LoadingSpinner } from "@/components/loading-spinner"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchSelectInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { cn } from "@/lib/utils"
import { stringRequired } from "@/models/common"
import { IcdCodeSeparator } from "@/modules/patients/detail/episode-of-care/icd-code-separator"
import { EpisodeOfCareFragment$data } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { IcdCodeSearchOption, IcdDiagnosisSearch } from "@/modules/patients/libs/icd-code-search-option"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"

export const episodeOfCareIcdCodeFormSchema = z.object({
  diagnosisCode: stringRequired,
})

export type EpisodeOfCareIcdCodeSchema = z.infer<typeof episodeOfCareIcdCodeFormSchema>

interface EpisodeOfCareIcdCodeFormProps {
  options: {
    commons: IcdDiagnosisSearch["commons"]
    others: IcdDiagnosisSearch["others"]
  }
  onChange: (value: string) => void
  isFetchingIcdCodes: boolean
  autoFocus?: boolean
  onFocusInput?: () => void
  selectedIcdCode?: EpisodeOfCareFragment$data["icdDiagnosis"] | null
  onOptionSelect?: (option: IcdDiagnosisFragment$data) => void
  onClear?: () => void
}

const EpisodeOfCareIcdCodeForm = ({
  options,
  selectedIcdCode = null,
  autoFocus = false,
  onChange,
  isFetchingIcdCodes,
  onFocusInput,
  onOptionSelect,
  onClear,
}: EpisodeOfCareIcdCodeFormProps) => {
  const { t } = useTranslation()

  const defaultSearchValue = selectedIcdCode?.diagnosisCode
    ? `${selectedIcdCode?.diagnosisCode} - ${selectedIcdCode?.text}`
    : ""
  const [searchValue, setSearchValue] = useState(defaultSearchValue)
  const [selectedDiagnosisCode, setSelectedDiagnosisCode] = useState(selectedIcdCode)

  const form = useFormContext<EpisodeOfCareIcdCodeSchema>()

  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const [searchResultVisible, setSearchResultVisible] = useState(false)

  const shouldShowFindOtherDiagnosis = useMemo(() => {
    if (!selectedDiagnosisCode) return false

    return selectedDiagnosisCode.isAssociation
  }, [selectedDiagnosisCode])

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    if (!searchResultVisible) setSearchResultVisible(true)
    setSearchValue(value)
    onChange(value)
  }

  const handleClearInput = () => {
    form.setValue("diagnosisCode", "")
    setSearchValue("")
    onChange("")
    setSelectedDiagnosisCode(null)
    searchRef.current?.focus()
    onFocusInput?.()
  }

  const handleSelectOption = (selectedOption: IcdDiagnosisFragment$data) => {
    form.setValue("diagnosisCode", selectedOption.diagnosisCode, { shouldValidate: true })
    setSearchValue(`${selectedOption.diagnosisCode} - ${selectedOption.text}`)
    setSelectedDiagnosisCode(selectedOption)
    onOptionSelect?.(selectedOption)

    setSearchResultVisible(false)
  }

  const handleFindOtherDiagnosis = () => {
    const primaryCode = selectedDiagnosisCode?.primaryCode ?? ""
    form.setValue("diagnosisCode", "")
    setSearchValue(primaryCode)
    onChange(primaryCode)
    searchRef.current?.focus()
  }

  useClickOutside((event: MouseEvent) => {
    if (searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      setSearchResultVisible(false)
    }
  }, searchRef)

  useEffect(() => {
    if (autoFocus && searchRef.current) {
      searchRef.current.focus()
      if (selectedIcdCode) {
        onChange(`${selectedIcdCode.diagnosisCode} - ${selectedIcdCode.text}`)
        setSearchResultVisible(false)
      }
    }
  }, [autoFocus, onChange, selectedIcdCode])

  return (
    <div>
      <FormField
        control={form.control}
        name="diagnosisCode"
        render={({ field: { name }, formState: { errors }, fieldState: { invalid } }) => (
          <>
            <SuggestionSearch>
              <SearchSelectInput
                ref={searchRef}
                label={t("episodeOfCare.progressNote.diagnosisCode")}
                required
                value={searchValue}
                error={invalid}
                onChange={handleChange}
                testId="episode-of-care"
                onClear={(event) => {
                  event.preventDefault()
                  handleClearInput()
                  onClear?.()
                }}
                onFocus={() => {
                  onFocusInput?.()
                  setSearchResultVisible(true)
                }}
              />

              <SearchContent
                ref={searchResultRef}
                className={cn(searchValue.length >= 3 && searchResultVisible ? "show" : "hidden", "px-0!")}
                data-testid="list-icd10-code">
                {isFetchingIcdCodes && (
                  <div className="my-2 flex w-full items-center justify-center">
                    <LoadingSpinner size={24} />
                  </div>
                )}
                {!isFetchingIcdCodes &&
                  (options.commons.length === 0 && options.others.length === 0 ? (
                    <div className="text-on-surface-subdued relative cursor-default px-2 text-sm select-none">
                      {t("common.nothingFound")}
                    </div>
                  ) : (
                    <ScrollArea>
                      <div className="p-2">
                        {!!options.commons.length && (
                          <>
                            <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                              {t("episodeOfCare.common")}
                            </div>
                            {options.commons.map((option) => (
                              <IcdCodeSearchOption
                                key={option.entry.diagnosisCode}
                                option={option.entry}
                                highlights={option.highlights}
                                onSelect={handleSelectOption}
                              />
                            ))}
                          </>
                        )}
                        {!!options.others.length && (
                          <>
                            {!!options.commons.length && <IcdCodeSeparator />}
                            <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                              {t("episodeOfCare.other")}
                            </div>
                            {options.others.map((option) => (
                              <IcdCodeSearchOption
                                key={option.entry.diagnosisCode}
                                option={option.entry}
                                highlights={option.highlights}
                                onSelect={handleSelectOption}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </ScrollArea>
                  ))}
              </SearchContent>
            </SuggestionSearch>
            <motion.div
              initial="closed"
              animate={invalid ? "open" : "closed"}
              exit="closed"
              variants={{
                closed: { height: 0 },
                open: { height: 20 },
              }}
              transition={{ type: "tween", duration: 0.15 }}>
              <TranslatedFieldError errors={errors} name={name} />
            </motion.div>
            {shouldShowFindOtherDiagnosis && (
              <div
                className="text-primary hover:text-primary-hovered mx-4 mt-2 cursor-pointer text-sm font-medium"
                onClick={handleFindOtherDiagnosis}>
                {t("episodeOfCare.progressNote.findOtherDiagnosis")}
              </div>
            )}
          </>
        )}
      />
    </div>
  )
}

export { EpisodeOfCareIcdCodeForm }
